import { P } from 'components'
import PMBInputNumber from 'components/PMBInputNumber'
import PMBSelect from 'components/PMBSelect'
import {
  getInventories,
  getInventorySelection,
  getPackings,
} from 'features/private-views/sell-commodity/sellCommodityAction'
import { setInitialTrx } from 'features/private-views/sell-commodity/sellCommoditySlice'
import { splitToManyInventory } from 'features/public-views/manageStock/API/manageStockAction'
import { capitalizeEveryWord } from 'helpers'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { Image, RefreshCw, Trash, X } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { setBatchInventory, toggleModal } from '../reportFulfilmentSlice'
import { SplitStockModal } from 'features/public-views/manageStock/Components'

const InventoryCard = React.memo(
  ({ inventory, isSelected, onToggleSelect }) => {
    return (
      <Card
        className={`mb-2 shadow-sm border-0 ${isSelected ? 'border border-primary' : ''}`}
        style={{ borderRadius: '12px' }}
        onClick={() => onToggleSelect(inventory.id)}
      >
        <CardBody className="py-3 px-3">
          <Row className="align-items-center g-3">
            <Col xs="auto" className="d-flex justify-content-center">
              <Input type="checkbox" checked={isSelected} readOnly />
            </Col>

            <Col xs="auto" className="d-flex justify-content-center">
              {inventory.linkFoto ? (
                <img
                  src={inventory.linkFoto}
                  alt={inventory.nameItem}
                  style={{
                    width: '48px',
                    height: '48px',
                    objectFit: 'cover',
                    borderRadius: '50%',
                    border: '1px solid #eee',
                  }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center bg-light"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    border: '1px solid #eee',
                  }}
                >
                  <Image size={18} className="text-muted" />
                </div>
              )}
            </Col>

            <Col xs={7} sm className="d-flex flex-column">
              <strong
                className="text-truncate"
                style={{ fontSize: '0.95rem', lineHeight: 1.3 }}
              >
                {inventory.nameItem}
              </strong>
              <small className="text-muted d-flex align-items-center mt-1">
                {inventory.id ? `ID: ${inventory.id}` : '-'}
                {inventory.lotCode ? ` • Lot ID: ${inventory.lotCode}` : ''}
                {inventory.bundle ? ` • ${inventory.bundle} Bundle` : ''}
              </small>

              <small className="text-muted mt-1">
                Stok: {inventory.amount} {inventory.unit}
              </small>
              {(inventory.projectLabel ||
                inventory.projectName ||
                (inventory.projectId ? `Project #${inventory.projectId}` : null)) && (
                <div className="mt-1">
                  <span
                    className="badge"
                    style={{
                      backgroundColor: '#E8F5E9',
                      color: '#2E7D32',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      display: 'inline-block',
                    }}
                  >
                    {inventory.projectLabel ||
                      inventory.projectName ||
                      `Project #${inventory.projectId}`}
                  </span>
                </div>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>
    )
  },
)

const ModalInvToTrx = ({ id, onRefresh, isOpen, onClose, items }) => {
  const dispatch = useDispatch()
  const toggle = () => dispatch(toggleModal())
  const { orders, invPacking, inventorySelection } = useSelector(
    (state) => state.sellCommodity,
  )

  const [selectedInventories, setSelectedInventories] = useState(new Set())
  const [inventoryAmounts, setInventoryAmounts] = useState({})
  const [invSplitData, setInvSplitData] = useState([]) // <-- ganti inventoryAmounts

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Split UI State
  const [showSplitModal, setShowSplitModal] = useState(false)
  const [splitSelectedStock, setSplitSelectedStock] = useState(null)
  const [splitItems, setSplitItems] = useState([])
  const [enableSplit, setEnableSplit] = useState(false)
  const [bulkSplitCount, setBulkSplitCount] = useState(1)
  const [showSplitConfirm, setShowSplitConfirm] = useState(false)
  const [confirmSplitPayload, setConfirmSplitPayload] = useState(null)
  const [pendingSplits, setPendingSplits] = useState({})
  const [showAll, setShowAll] = useState(false)
  const [filteredList, setFilteredList] = useState([])
  const [allList, setAllList] = useState(null)
  const [isLoadingAll, setIsLoadingAll] = useState(false)
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)

  const handleToggleShowAll = async (e) => {
    const isChecked = e?.target?.checked ?? !showAll
    setShowAll(isChecked)

    if (isChecked && !allList) {
      try {
        setIsLoadingAll(true)
        const res = await dispatch(getInventorySelection({ itemIds: '' })).unwrap()
        const list = Array.isArray(res) ? res : res?.data || []
        setAllList(list)
      } catch (err) {
        enqueueSnackbar('Gagal mengambil daftar semua inventori', { variant: 'error' })
        setShowAll(false)
      } finally {
        setIsLoadingAll(false)
      }
    }
  }

  const { enqueueSnackbar } = useSnackbar()

  const addSplitItem = (data, key, amount = 1) => {
    if (!data?.id) {
      data = splitSelectedStock
    }

    if (splitItems?.[0]?.amount === 1) {
      return
    }

    const totalNewCount = amount
    const lockedItems = splitItems?.filter((item) => item.locked) || []

    const totalLockedAmount = lockedItems.reduce(
      (sum, item) => sum + parseFloat(item.amount || 0),
      0,
    )

    const remainingAmount = parseFloat(data?.amount || 0) - totalLockedAmount

    const baseItem = {
      nameItem:
        data?.nameItem || data?.SKU || data?.item?.nameItem || data?.item?.SKU,
      itemId: data?.itemId || data?.item?.id,
      unit: data?.unit,
      desc: data?.description || '',
    }

    const newSplitItems = [...splitItems]

    for (let i = 0; i < totalNewCount; i++) {
      newSplitItems.push({
        ...baseItem,
        amount: 0,
        locked: false,
      })
    }

    const updatedUnlockedItems = newSplitItems?.filter((item) => !item.locked)
    const unlockedCount = updatedUnlockedItems.length

    const base = Math.floor(remainingAmount / unlockedCount)
    const remainder = remainingAmount % unlockedCount

    let uIdx = 0
    const distributedItems = newSplitItems.map((item) => {
      if (item.locked) return item

      let amountAdded = base
      if (uIdx < Math.floor(remainder)) {
        amountAdded = base + 1
      } else if (uIdx === Math.floor(remainder) && remainder % 1 > 0) {
        amountAdded = base + (remainder % 1)
      }
      uIdx++

      return {
        ...item,
        amount: amountAdded,
      }
    })

    setSplitItems(distributedItems)
    setEnableSplit(distributedItems[0]?.amount > 1)
  }

  const onChangeValueStock = (key, val, index) => {
    const list = [...splitItems]

    if (key === 'amount') {
      let newValue = parseFloat(String(val).replace(',', '.') || 0)
      const totalAmount = parseFloat(splitSelectedStock?.amount || 0)
      const lockedItems = list.filter((item, i) => item.locked && i !== index)
      const totalLockedAmount = lockedItems.reduce(
        (sum, item) => sum + parseFloat(item.amount || 0),
        0,
      )
      const remainingAmount = totalAmount - totalLockedAmount

      if (newValue > remainingAmount) {
        newValue = remainingAmount
      }

      list[index] = {
        ...list[index],
        amount: newValue,
      }

      const unlockedItems = list.filter((item, i) => !item.locked && i !== index)
      const unlockedCount = unlockedItems.length

      if (unlockedCount > 0) {
        const newRemainingAmount = remainingAmount - newValue
        const base = Math.floor(newRemainingAmount / unlockedCount)
        const remainder = newRemainingAmount % unlockedCount

        let uIdx = 0
        for (let i = 0; i < list.length; i++) {
          if (!list[i].locked && i !== index) {
            let amountAdded = base
            if (uIdx < Math.floor(remainder)) {
              amountAdded = base + 1
            } else if (uIdx === Math.floor(remainder) && remainder % 1 > 0) {
              amountAdded = base + (remainder % 1)
            }
            list[i].amount = amountAdded
            uIdx++
          }
        }
      }
    } else {
      list[index] = {
        ...list[index],
        [key]: val,
      }
    }
    setSplitItems(list)
  }

  const onLockValue = (item) => {
    const list = [...splitItems]
    const index = list.findIndex((el) => el === item)
    list[index].locked = !list[index].locked
    setSplitItems(list)
  }

  const handleOpenSplitModal = (opt) => {
    const data = opt.data
    const targetItem = {
      id: data.id,
      amount: data.amount,
      unit: data.unit,
      nameItem: data.nameItem,
      itemId: data.itemId || data.itemsId,
      linkFoto: data.linkFoto,
      item: {
        id: data.itemId || data.itemsId,
        nameItem: data.nameItem,
        linkFoto: data.linkFoto,
      }
    }
    setSplitSelectedStock(targetItem)

    if (pendingSplits[data.id]) {
      const savedSplits = pendingSplits[data.id].arrNew.map((a) => ({
        nameItem: data.nameItem,
        itemId: data.itemId || data.itemsId || data.item?.id,
        unit: a.unit,
        amount: a.amount,
        ownerCode: a.ownerCode || data.ownerCode || '',
        desc: a.desc || data.description || '',
        locked: false,
      }))
      setSplitItems(savedSplits)
      setEnableSplit(true)
    } else {
      setSplitItems([
        {
          nameItem: data.nameItem,
          itemId: data.itemId || data.itemsId || data.item?.id,
          unit: data.unit,
          amount: data.amount,
          ownerCode: data.ownerCode || '',
          desc: data.description || '',
          locked: false,
        },
      ])
      setEnableSplit(data.amount > 1)
    }

    setShowSplitModal(true)
  }

  const handleSplitSubmit = () => {
    let finalSplitItems = splitItems

    // Auto-split if user typed in the stepper but forgot to click 'Tambah Bagian' button
    if (splitItems.length === 1 && bulkSplitCount > 0) {
      const data = splitSelectedStock
      const remainingAmount = parseFloat(data?.amount || 0)
      const totalNewCount = bulkSplitCount

      const baseItem = {
        nameItem: data?.nameItem || data?.SKU || data?.item?.nameItem || data?.item?.SKU,
        itemId: data?.itemId || data?.itemsId || data?.item?.id,
        unit: data?.unit,
        ownerCode: data?.ownerCode || '',
        desc: data?.description || '',
      }

      const newSplitItems = [...splitItems]
      for (let i = 0; i < totalNewCount; i++) {
        newSplitItems.push({
          ...baseItem,
          amount: 0,
          locked: false,
        })
      }

      const unlockedCount = newSplitItems.length
      const base = Math.floor(remainingAmount / unlockedCount)
      const remainder = remainingAmount % unlockedCount

      let uIdx = 0
      finalSplitItems = newSplitItems.map((item) => {
        let amountAdded = base
        if (uIdx < Math.floor(remainder)) {
          amountAdded = base + 1
        } else if (uIdx === Math.floor(remainder) && remainder % 1 > 0) {
          amountAdded = base + (remainder % 1)
        }
        uIdx++
        return {
          ...item,
          amount: amountAdded,
        }
      })
    }

    if (finalSplitItems.length <= 1) {
      enqueueSnackbar('Silakan tambahkan bagian split terlebih dahulu', { variant: 'warning' })
      return
    }

    const payload = {
      inventoryId: splitSelectedStock.id,
      arrNew: finalSplitItems.map((a) => ({
        amount: Number(String(a.amount).replace(',', '.')),
        desc: a.desc || a.description || splitSelectedStock?.description || '',
        itemId: a.itemId || splitSelectedStock?.itemId || splitSelectedStock?.itemsId || splitSelectedStock?.item?.id,
        ownerCode: a.ownerCode ?? splitSelectedStock?.ownerCode ?? '',
        unit: String(a.unit?.value || a.unit || splitSelectedStock?.unit || ''),
        ...((a?.bundle || splitSelectedStock?.bundle) && {
          bundle: Number(a?.bundle || splitSelectedStock?.bundle),
        }),
      })),
      notes: `Manual split for fulfilment ${id}`,
    }

    setConfirmSplitPayload(payload)
    setShowSplitConfirm(true)
  }

  const executeSplit = async () => {
    try {
      setIsSubmitting(true)
      const res = await dispatch(splitToManyInventory(confirmSplitPayload)).unwrap()

      let newInventories = []
      if (Array.isArray(res)) newInventories = res
      else if (Array.isArray(res?.data)) newInventories = res.data
      else if (Array.isArray(res?.items)) newInventories = res.items
      else if (Array.isArray(res?.result)) newInventories = res.result
      else if (Array.isArray(res?.newInventories)) newInventories = res.newInventories
      else if (Array.isArray(res?.data?.items)) newInventories = res.data.items
      else if (Array.isArray(res?.data?.result)) newInventories = res.data.result
      else if (Array.isArray(res?.data?.data)) newInventories = res.data.data

      if (newInventories && newInventories.length > 0) {
        setPendingSplits((prev) => ({
          ...prev,
          [confirmSplitPayload.inventoryId]: { arrNew: newInventories },
        }))
        enqueueSnackbar('Berhasil memisah inventori', { variant: 'success' })

        const itemIdsString = items?.map((i) => i.itemsId).join(',') || ''
        const resSplit = await dispatch(getInventorySelection({ itemIds: itemIdsString })).unwrap()
        const listSplit = Array.isArray(resSplit) ? resSplit : resSplit?.data || []
        setFilteredList(listSplit)
      } else {
        enqueueSnackbar('Gagal memisah inventori: Respons tidak valid', { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? error?.message ?? 'Gagal memisah inventori', { variant: 'error' })
    } finally {
      setIsSubmitting(false)
      setShowSplitConfirm(false)
      setShowSplitModal(false)
      setSplitItems([])
      setBulkSplitCount(1)
      setConfirmSplitPayload(null)
    }
  }

  const currentInventoryData = showAll ? (allList || []) : filteredList

  const inventoryOptions = (currentInventoryData || []).map((inv) => ({
    value: inv.id,
    label: `${inv?.nameItem} (${inv?.lotCode || '-'}) - ${inv?.amount}${inv?.unit}${inv?.ownerCode ? ` | Kode Inventori: ${inv.ownerCode}` : ''
      }`,
    data: inv,
    image: inv?.linkFoto || inv?.image || inv?.foto,
    customLabel: {
      itemName: inv?.nameItem || '-',
      ownerCode: inv?.ownerCode || '-',
      lotCode: inv?.lotCode || '-',
      bundle: inv?.bundle,
      amount: inv?.amount || 0,
      unit: inv?.unit || '',
      imageUrl: inv?.linkFoto || inv?.image || inv?.foto,
      warehouseName: inv?.warehouseName || '-',
    },
  }))

  const handleSelectChange = (selected) => {
    const newSelectedSet = new Set(selected.map((opt) => opt.value))

    const fulfilledByCommodity = {}

    const newSplitData = selected.map((opt) => {
      const existing = invSplitData.find((i) => i.inventoryId === opt.value)
      const matchKey = opt.data.itemsId || opt.data.itemId || opt.data.nameItem

      if (existing) {
        if (matchKey) {
          fulfilledByCommodity[matchKey] =
            (fulfilledByCommodity[matchKey] || 0) +
            parseFloat(existing.splitAmount || 0)
        }
        return {
          inventoryId: opt.value,
          splitAmount: existing.splitAmount,
        }
      }

      let autoAmount = opt.data.amount
      if (items?.length) {
        const matchedItem = items.find(
          (item) =>
            item.itemsId === opt.data.itemsId ||
            item.itemId === opt.data.itemId ||
            item.nameItem === opt.data.nameItem ||
            item.label === opt.data.nameItem,
        )
        if (matchedItem) {
          const alreadyFulfilled = matchKey ? (fulfilledByCommodity[matchKey] || 0) : 0
          const needed = Math.max(
            0,
            parseFloat(matchedItem.orderAmount || 0) -
            parseFloat(matchedItem.invAmount || 0) -
            alreadyFulfilled,
          )
          autoAmount = Math.min(needed, parseFloat(opt.data.amount || 0))

          if (matchKey) {
            fulfilledByCommodity[matchKey] = alreadyFulfilled + autoAmount
          }
        }
      }

      return {
        inventoryId: opt.value,
        splitAmount: autoAmount,
      }
    })

    setInvSplitData(newSplitData)
    setSelectedInventories(newSelectedSet)
  }

  const selectedOptions = inventoryOptions.filter((opt) =>
    selectedInventories.has(opt.value),
  )

  const handleAmountChange = (inventoryId, newAmount, isFromSplit = false) => {
    setInvSplitData((prev) =>
      prev.map((item) =>
        item.inventoryId === inventoryId
          ? { ...item, splitAmount: newAmount }
          : item,
      ),
    )

    if (!isFromSplit && pendingSplits[inventoryId]) {
      setPendingSplits(prev => {
        const newPending = { ...prev }
        delete newPending[inventoryId]
        return newPending
      })
    }
  }

  const handleRemoveSplitPart = (inventoryId, idxToRemove) => {
    setPendingSplits((prev) => {
      const current = prev[inventoryId]
      if (!current) return prev

      const newArr = current.arrNew.filter((_, idx) => idx !== idxToRemove)

      if (newArr.length === 0) {
        return {
          ...prev,
          [inventoryId]: {
            ...current,
            arrNew: newArr,
          },
        }
      }

      return {
        ...prev,
        [inventoryId]: {
          ...current,
          arrNew: newArr,
        },
      }
    })
  }

  const handleUpdateSplitPartAmount = (inventoryId, idxToUpdate, newAmount) => {
    setPendingSplits((prev) => {
      const current = prev[inventoryId]
      if (!current) return prev

      const updatedArr = current.arrNew.map((part, idx) =>
        idx === idxToUpdate ? { ...part, amount: newAmount } : part,
      )

      return {
        ...prev,
        [inventoryId]: {
          ...current,
          arrNew: updatedArr,
        },
      }
    })
  }

  const handleDeselectInventory = (inventoryId) => {
    setSelectedInventories((prev) => {
      const next = new Set(prev)
      next.delete(inventoryId)
      return next
    })

    setInvSplitData((prev) => prev.filter((i) => i.inventoryId !== inventoryId))

    setPendingSplits((prev) => {
      const newPending = { ...prev }
      delete newPending[inventoryId]
      return newPending
    })
  }

  const handleSubmit = async () => {
    try {
      if (selectedInventories.size === 0) {
        enqueueSnackbar('Pilih setidaknya satu inventori', {
          variant: 'warning',
        })
        return
      }

      setIsSubmitting(true)

      const selectedInventoryIds = Array.from(selectedInventories)
      const idsPacking = invPacking?.map((e) => e?.inventoryId) || []
      const idsInv = orders?.item?.map((e) => e?.inventoryId) || []

      const newInvSplitData = []

      for (const item of invSplitData) {
        if (!selectedInventories.has(item.inventoryId)) continue

        const pendingSplit = pendingSplits[item.inventoryId]

        if (pendingSplit) {
          if (pendingSplit.arrNew && pendingSplit.arrNew.length > 0) {
            pendingSplit.arrNew.forEach((inv) => {
              newInvSplitData.push({
                inventoryId: inv.id,
                splitAmount: inv.amount,
              })
            })
          }
        } else {
          newInvSplitData.push(item)
        }
      }

      const body = {
        escrowpaymentId: id,
        invSplitData: newInvSplitData,
      }

      const resp = await dispatch(setBatchInventory(body)).unwrap()

      if (!resp?.data?.error) {
        dispatch(setInitialTrx())
        toggle()
        enqueueSnackbar('Proses inventori ke pesanan berhasil.', {
          variant: 'success',
        })
        onRefresh()
        setSelectedInventories(new Set())
        setInvSplitData([])
        setPendingSplits({})
        onClose()
      } else {
        throw resp
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? error?.message, {
        variant: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const itemIdsString = items?.map((i) => i.itemsId).join(',') || ''
      try {
        setIsLoadingInitial(true)
        const res = await Promise.all([
          dispatch(getInventories()),
          dispatch(getPackings()),
          dispatch(getInventorySelection({ itemIds: itemIdsString })).unwrap(),
        ])
        if (res[2]) {
          const list = Array.isArray(res[2]) ? res[2] : res[2]?.data || []
          setFilteredList(list)
        }
      } catch (e) {
        // Handled silently
      } finally {
        setIsLoadingInitial(false)
      }
    }

    if (isOpen) {
      fetchData()
    }
  }, [isOpen, dispatch, items])

  useEffect(() => {
    if (!isOpen) {
      setSelectedInventories(new Set())
      setInvSplitData([])
      setPendingSplits({})
      setShowAll(false)
      setAllList(null)
      setFilteredList([])
    }
  }, [isOpen])

  const formatOptionLabel = (option) => {
    if (option.customLabel) {
      const { itemName, ownerCode, lotCode, amount, unit, warehouseName } =
        option.customLabel

      return (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
          <div
            style={{ width: '100%', wordWrap: 'break-word' }}
            className="mb-2 mb-md-0"
          >
            <div>{itemName}</div>
            <div
              className="text-muted"
              style={{ fontSize: '0.85em', marginTop: '2px' }}
            >
              <span>Kode Inventori: {ownerCode ?? '-'}</span>
              <span className="ms-2 me-2">•</span>
              <span>Lot ID: {lotCode ?? '-'}</span>
              {(option?.customLabel?.bundle || option?.data?.bundle) && (
                <>
                  <span className="ms-2 me-2">•</span>
                  <span>Bundle: {option?.customLabel?.bundle || option?.data?.bundle}</span>
                </>
              )}
            </div>
          </div>
          <div
            className="text-muted d-flex flex-wrap align-items-center justify-content-start justify-content-md-end"
            style={{ width: '100%', gap: '0.5rem' }}
          >
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {newFormatThousandSeparator(amount || 0)}{' '}
              {capitalizeEveryWord(unit)}
            </span>
            <span className="d-none d-md-inline">|</span>
            <span style={{ wordWrap: 'break-word' }}>{warehouseName}</span>
          </div>
        </div>
      )
    }

    const imageUrl =
      option.image ||
      option.data?.linkFoto ||
      option.data?.image ||
      option.data?.foto

    return (
      <div className="d-flex align-items-center gap-3 py-2">
        <div className="flex-shrink-0">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={option.data?.nameItem || 'Item'}
              style={{
                width: 40,
                height: 40,
                objectFit: 'cover',
                borderRadius: '50%',
                border: '1px solid #e5e7eb',
              }}
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.nextSibling.style.display = 'flex'
              }}
            />
          ) : null}
        </div>

        <div className="d-flex flex-column flex-grow-1">
          <div className="fw-semibold text-dark">{option.label}</div>
        </div>
      </div>
    )
  }

  const hasInvalidSelection = Array.from(selectedInventories).some((id) => {
    const pendingSplit = pendingSplits[id]
    return pendingSplit && pendingSplit.arrNew && pendingSplit.arrNew.length === 0
  })

  return (
    <>
      <Modal centered data-testid="fulfilment-modal-inv-to-trx" isOpen={isOpen} toggle={toggle} size="lg">
        <ModalBody
          className="p-4"
          style={{ maxHeight: '90vh', overflowY: 'auto' }}
        >
          <P className="fw-bold mb-3">Proses Inventori ke Pesanan</P>
          <FormGroup>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <P className="text-sm pb-0 mb-0" size="regular" color="#344054">
                Pilih Inventori<span style={{ color: '#b42218' }}>*</span>
              </P>
              <div className="d-flex align-items-center gap-2">
                {isLoadingAll && <Spinner size="sm" color="primary" />}
                <div
                  className="d-flex align-items-center gap-2 px-2 py-1 rounded-3"
                  style={{
                    backgroundColor: showAll ? '#e0f2fe' : '#f8f9fa',
                    border: showAll ? '1px solid #bae6fd' : '1px solid #e9ecef',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <FormGroup switch className="mb-0 ps-0 d-flex align-items-center" style={{ minHeight: 'auto' }}>
                    <Input
                      type="switch"
                      role="switch"
                      id="toggleShowAllInventories"
                      data-testid="fulfilment-show-all-inventories-toggle"
                      checked={showAll}
                      onChange={handleToggleShowAll}
                      disabled={isLoadingAll || isLoadingInitial || isSubmitting}
                      style={{
                        cursor: 'pointer',
                        marginLeft: 0,
                        marginTop: 0,
                        width: '2.6rem',
                        height: '1.35rem',
                      }}
                    />
                  </FormGroup>
                  <Label
                    for="toggleShowAllInventories"
                    className="mb-0 pointer user-select-none fw-semibold"
                    style={{
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: showAll ? '#0284c7' : '#344054',
                    }}
                  >
                    Tampilkan semua inventori
                  </Label>
                </div>
              </div>
            </div>
            <PMBSelect
              isMulti
              testId="fulfilment-inventory-select"
              options={inventoryOptions}
              value={selectedOptions}
              onChange={handleSelectChange}
              formatOptionLabel={formatOptionLabel}
              getOptionLabel={(e) => e.label}
              placeholder={
                isLoadingAll || isLoadingInitial
                  ? 'Memuat data inventori...'
                  : 'Pilih inventori...'
              }
              isLoading={isLoadingAll || isLoadingInitial}
              isDisabled={isLoadingAll || isLoadingInitial || isSubmitting}
              noOptionsMessage={() =>
                isLoadingAll || isLoadingInitial
                  ? 'Memuat data inventori...'
                  : showAll
                  ? 'Tidak ada inventori yang tersedia'
                  : 'Tidak ada inventori yang cocok dengan item pesanan'
              }
              name="productProcess"
              style={{ width: '100%' }}
              menuPosition="fixed"
              menuPlacement="bottom"
              styles={{
                control: (provided) => ({
                  ...provided,
                  borderColor: '#d1d5db',
                  boxShadow: 'none',
                  '&:hover': { borderColor: '#d1d5db' },
                  borderRadius: '8px',
                  padding: '2px',
                  minWidth: '100%',
                  maxWidth: '100%',
                }),
                valueContainer: (base) => ({
                  ...base,
                  maxHeight: 120,
                  overflowY: 'auto',
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: '#e0f2ff',
                  borderRadius: '12px',
                  padding: '2px 6px',
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: '#0369a1',
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: '#0369a1',
                  '&:hover': {
                    backgroundColor: '#b3e5fc',
                    color: '#005f8e',
                  },
                }),
                dropdownIndicator: (provided) => ({
                  ...provided,
                  color: '#6b7280',
                }),
                clearIndicator: (provided) => ({ ...provided, color: '#6b7280' }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                option: (provided) => ({
                  ...provided,
                  padding: '8px 12px',
                }),
              }}
            />
            {!showAll && !isLoadingInitial && !isLoadingAll && inventoryOptions.length === 0 && (
              <small className="text-muted mt-1 d-block">
                Tidak ada inventori yang cocok dengan item pesanan ini.{' '}
                <span
                  className="text-primary pointer fw-semibold"
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => handleToggleShowAll({ target: { checked: true } })}
                >
                  Aktifkan sakelar "Tampilkan semua inventori" di atas.
                </span>
              </small>
            )}
          </FormGroup>

          {selectedOptions.length > 0 && (
            <div className="mt-3">
              <h6 className="fw-semibold mb-2">
                {selectedOptions.length} Inventori yang dipilih
              </h6>
              <div className="d-flex flex-column gap-2">
                {selectedOptions.map((opt, index) => {
                  let dynamicMax = opt.data.amount
                  if (items?.length) {
                    const matchKey =
                      opt.data.itemsId || opt.data.itemId || opt.data.nameItem
                    const matchedItem = items.find(
                      (item) =>
                        item.itemsId === opt.data.itemsId ||
                        item.itemId === opt.data.itemId ||
                        item.nameItem === opt.data.nameItem ||
                        item.label === opt.data.nameItem,
                    )

                    if (matchedItem) {
                      const otherSelectedAmount = selectedOptions
                        .filter((otherOpt) => otherOpt.value !== opt.value)
                        .filter((otherOpt) => {
                          const otherMatchKey =
                            otherOpt.data.itemsId ||
                            otherOpt.data.itemId ||
                            otherOpt.data.nameItem
                          return otherMatchKey === matchKey
                        })
                        .reduce((sum, otherOpt) => {
                          const otherSplit =
                            invSplitData.find(
                              (i) => i.inventoryId === otherOpt.value,
                            )?.splitAmount || 0
                          return sum + parseFloat(otherSplit)
                        }, 0)

                      const needed = Math.max(
                        0,
                        parseFloat(matchedItem.orderAmount || 0) -
                        parseFloat(matchedItem.invAmount || 0) -
                        otherSelectedAmount,
                      )

                      dynamicMax = Math.min(
                        parseFloat(opt.data.amount || 0),
                        needed,
                      )
                    }
                  }

                  if (pendingSplits[opt.value]) {
                    const pendingGroup = pendingSplits[opt.value]
                    if (pendingGroup.arrNew.length === 0) {
                      return (
                        <div
                          key={opt.value}
                          className="d-flex flex-column gap-2 p-3 text-center border rounded bg-light"
                        >
                          <div className="text-muted small fst-italic">
                            Semua bagian telah dihapus.
                          </div>
                          <div>
                            <Button
                              size="sm"
                              color="primary"
                              outline
                              onClick={() =>
                                handleAmountChange(
                                  opt.value,
                                  opt.customLabel.amount || 0,
                                )
                              }
                            >
                              Gunakan Stok Tersedia ({opt.customLabel.amount} {opt.customLabel.unit})
                            </Button>
                          </div>
                        </div>
                      )
                    }

                    return pendingGroup.arrNew.map((splitItem, idx) => (
                      <div
                        key={`${opt.value}-${idx}`}
                        className="d-flex flex-column gap-2 p-2 border rounded"
                        style={{ backgroundColor: '#f9fafb' }}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div className="d-flex align-items-start gap-2 flex-grow-1">
                            {opt.customLabel.imageUrl && (
                              <img
                                src={opt.customLabel.imageUrl}
                                alt={opt.customLabel.itemName}
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 8,
                                  objectFit: 'cover',
                                }}
                              />
                            )}
                            <div className="flex-grow-1">
                              <div className="fw-semibold">
                                {opt.customLabel.itemName}
                              </div>
                              <div className="text-muted small">
                                Kode Inventori: {splitItem.ownerCode || opt.customLabel.ownerCode || '-'} • Lot ID:{' '}
                                {opt.customLabel.lotCode || '-'}
                                {(splitItem.bundle || opt.customLabel.bundle) ? ` • Bundle: ${splitItem.bundle || opt.customLabel.bundle}` : ''}
                              </div>
                              <div className="text-muted small">
                                Stok Tersedia:{' '}
                                {newFormatThousandSeparator(splitItem.amount || 0)}{' '}
                                {capitalizeEveryWord(splitItem.unit || opt.customLabel.unit)}
                              </div>
                              <div className="text-muted small">
                                Gudang: {opt.customLabel.warehouseName || '-'}
                              </div>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2" style={{ width: 160 }}>
                            <PMBInputNumber
                              onFocus={(e) => e.target.select()}
                              testId={`fulfilment-amount-input-${opt.value}-split-${idx}`}
                              name={`split-amount-${opt.value}-${idx}`}
                              placeholder="Jumlah"
                              prefix={capitalizeEveryWord(splitItem.unit || opt.customLabel.unit)}
                              isFirstPrefix={false}
                              value={newFormatThousandSeparator(splitItem.amount ?? 0)}
                              onChange={(e) =>
                                handleUpdateSplitPartAmount(
                                  opt.value,
                                  idx,
                                  Number(e.target.value),
                                )
                              }
                            />
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              className="p-1 d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{ width: 32, height: 32, borderRadius: 6 }}
                              onClick={() => handleRemoveSplitPart(opt.value, idx)}
                              title="Hapus bagian ini"
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  }

                  return (
                    <div
                      key={opt.value}
                      className="d-flex flex-column gap-2 p-2 border rounded"
                      style={{ backgroundColor: '#f9fafb' }}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-start gap-2 flex-grow-1">
                          {opt.customLabel.imageUrl && (
                            <img
                              src={opt.customLabel.imageUrl}
                              alt={opt.customLabel.itemName}
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                objectFit: 'cover',
                              }}
                            />
                          )}
                          <div className="flex-grow-1">
                            <div className="fw-semibold">
                              {opt.customLabel.itemName}
                            </div>
                            <div className="text-muted small">
                              Kode Inventori: {opt.customLabel.ownerCode} • Lot ID:{' '}
                              {opt.customLabel.lotCode || '-'}
                              {opt.customLabel.bundle ? ` • Bundle: ${opt.customLabel.bundle}` : ''}
                            </div>
                            <div className="text-muted small">
                              Stok Tersedia:{' '}
                              {newFormatThousandSeparator(
                                opt.customLabel.amount || 0,
                              )}{' '}
                              {capitalizeEveryWord(opt.customLabel.unit)}
                            </div>
                            <div className="text-muted small">
                              Gudang: {opt.customLabel.warehouseName || '-'}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2" style={{ width: 160 }}>
                          <div className="d-flex flex-column flex-grow-1">
                            <PMBInputNumber
                              onFocus={(e) => e.target.select()}
                              testId={`fulfilment-amount-input-${opt.value}`}
                              name={`amount-${opt.value}`}
                              placeholder="Jumlah"
                              prefix={capitalizeEveryWord(opt.customLabel.unit)}
                              isFirstPrefix={false}
                              value={newFormatThousandSeparator(
                                invSplitData.find((i) => i.inventoryId === opt.value)
                                  ?.splitAmount ?? (opt.customLabel.amount || 0),
                              )}
                              onChange={(e) =>
                                handleAmountChange(opt.value, Number(e.target.value))
                              }
                              disabled={!!pendingSplits[opt.value]}
                            />
                            {opt.data.amount > 1 && (
                              <Button
                                size="sm"
                                color="link"
                                data-testid={`fulfilment-split-button-${opt.value}`}
                                className="p-0 mt-1 fw-bold text-start"
                                style={{ fontSize: 13, textDecoration: 'none' }}
                                onClick={() => handleOpenSplitModal(opt)}
                              >
                                Pisah Inventori
                              </Button>
                            )}
                          </div>
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            className="p-1 d-flex align-items-center justify-content-center flex-shrink-0 align-self-start mt-1"
                            style={{ width: 32, height: 32, borderRadius: 6 }}
                            onClick={() => handleDeselectInventory(opt.value)}
                            title="Hapus inventori ini"
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="p-3">
          <Button outline data-testid="fulfilment-modal-cancel-button" onClick={onClose} style={{ flex: 1 }}>
            Batal
          </Button>
          <Button
            color="primary"
            data-testid="fulfilment-modal-save-button"
            style={{ flex: 1 }}
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              selectedInventories.size === 0 ||
              hasInvalidSelection
            }
          >
            {isSubmitting ? <Spinner size="sm" /> : 'Simpan'}
          </Button>
        </ModalFooter>

        {/* Confirmation Modal */}
        <Modal
          isOpen={showSplitConfirm}
          toggle={() => setShowSplitConfirm(false)}
          centered
        >
          <div className="modal-header">
            <h5 className="modal-title">Konfirmasi Pemisahan Inventori</h5>
            <X
              size={20}
              style={{ cursor: 'pointer' }}
              onClick={() => setShowSplitConfirm(false)}
            />
          </div>
          <ModalBody>
            <p>
              Apakah Anda yakin ingin memisah inventori ini? Setelah dipisah,
              inventori tidak dapat dikembalikan seperti semula dan akan terpisah
              selamanya.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              outline
              color="primary"
              onClick={() => setShowSplitConfirm(false)}
            >
              Batal
            </Button>
            <Button
              color="primary"
              onClick={executeSplit}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner size="sm" /> : 'Ya, Pisah'}
            </Button>
          </ModalFooter>
        </Modal>
      </Modal>

      <Modal
        isOpen={showSplitModal}
        toggle={() => setShowSplitModal(false)}
        size="lg"
        scrollable
      >
        <ModalHeader toggle={() => setShowSplitModal(false)}>
          <span className="fw-bold">Pisah Inventori</span>
        </ModalHeader>
        <ModalBody className="bg-light">
          <SplitStockModal
            isOpen={showSplitModal}
            selectedStock={splitSelectedStock}
            splitItems={splitItems}
            enableSplit={enableSplit}
            addSplitItem={() => {
              addSplitItem('', '', bulkSplitCount)
              setBulkSplitCount(1)
            }}
            onShowItem={(type, val) => setShowSplitModal(val)}
            setIndexSelectedSplitStock={() => { }}
            onChangeValue={onChangeValueStock}
            onSplitStock={handleSplitSubmit}
            loading={false}
            bulkSplitCount={bulkSplitCount}
            setBulkSplitCount={setBulkSplitCount}
            onLockValue={onLockValue}
          />
        </ModalBody>
      </Modal>
    </>
  )
}

export default ModalInvToTrx